import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Client } from '../models/Client';
import { ClientStatus } from '../models/enums';
import { clientService } from '../services/dataverse/clientService';

export interface ClientFilters {
  search: string;
  status: 'all' | ClientStatus;
}

export const useClients = () => {
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ClientFilters>({ search: '', status: 'all' });

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await clientService.list();
      setAllClients(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load clients.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  // Client-side filtering — no API call on every keystroke
  const clients = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return allClients
      .filter((item) => filters.status === 'all' || item.cr_crm_status === filters.status)
      .filter((item) => !term || [item.cr_crm_companyname, item.cr_crm_domain, item.cr_crm_email]
        .filter(Boolean).some((v) => v?.toLowerCase().includes(term)));
  }, [allClients, filters]);

  const createClient = useCallback(async (payload: Omit<Client, 'cr_crm_clientid'>) => {
    const created = await clientService.create(payload);
    await loadClients();
    return created;
  }, [loadClients]);

  const updateClient = useCallback(async (id: string, payload: Partial<Client>) => {
    const updated = await clientService.update(id, payload);
    await loadClients();
    return updated;
  }, [loadClients]);

  const deleteClient = useCallback(async (id: string) => {
    await clientService.delete(id);
    await loadClients();
  }, [loadClients]);

  const syncClient = useCallback(async (id: string) => {
    const updated = await clientService.sync(id);
    await loadClients();
    return updated;
  }, [loadClients]);

  return { clients, loading, error, filters, setFilters, reload: loadClients, createClient, updateClient, deleteClient, syncClient };
};
